import React from "react";
import {Link} from "react-router-dom";
import {
    createTable,
    PaginationState,
    RenderingPresetType,
    SortingState,
    TableColumnSpec,
    TableHeaderSpec
} from "../../../../../components/table/CreateTable.tsx";
import {BankAccountLogo} from "../../../../../components/general/BankAccountLogo.tsx";
import {Transaction} from "../../TransactionsTable.tsx";
import {CustomerBankAccount} from "../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import {MoneyLabel} from "../../../../../components/general/MoneyLabel.tsx";
import GLAPill from "../../../../../components/specific/GLAPill.tsx";
import {Tag} from "../../../../../components/general/Tag.tsx";
import {vatCodeToBadgeLabel} from "./vatCodeLabels.ts";
import {RiExchangeLine} from "react-icons/ri";
import {
    AdjustmentsHorizontalIcon,
    ArrowsRightLeftIcon,
    BanknotesIcon,
    BoltIcon,
    CreditCardIcon,
    LinkIcon,
    QuestionMarkCircleIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";
import {BackendAPITypes} from "../../../../../generated/blueprint.ts";
import {CategorizationSourceEnum} from "../../../../../types/enums.ts";
import {formatDate} from "../../../../../lib/utils.ts";
import {automationRuleSettingsPath, TransactionStatusCell} from "./TransactionStatusCell.tsx";


export enum TransactionsTableViewerRole {
    ENTREPRENEUR = "entrepreneur",
    PARTNER = "partner",
}

export type CategorizationChange =
    BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerTransactionsCategorizationChangePostResponseBody[number];

const CATEGORIZATION_SOURCE_LABELS: Record<CategorizationSourceEnum, string> = {
    [CategorizationSourceEnum.HUMAN]: "Handmatig",
    [CategorizationSourceEnum.CATEGORIZATION_ENGINE]: "Automatisch gecategoriseerd",
    [CategorizationSourceEnum.MATCH_DERIVATION]: "Afgeleid van koppeling",
    [CategorizationSourceEnum.MERGE]: "Samengevoegde transactie",
    [CategorizationSourceEnum.BUSINESS_RULE]: "Bedrijfsregel",
    [CategorizationSourceEnum.AUTOMATION_RULE]: "Automatiseringsregel",
    [CategorizationSourceEnum.AUTOMATION_RULE_BACKFILL]: "Automatiseringsregel (terugwerkend)",
    [CategorizationSourceEnum.INTERNAL_TRANSFER_DETECTION]: "Interne overboeking",
    [CategorizationSourceEnum.SAVINGS_MIRROR]: "Spaarrekening spiegeling",
    [CategorizationSourceEnum.CREDIT_CARD_SETTLEMENT]: "Creditcardafwikkeling",
    [CategorizationSourceEnum.UNKNOWN]: "Onbekend",
};

const CATEGORIZATION_SOURCE_ICONS: Record<CategorizationSourceEnum, typeof SparklesIcon> = {
    [CategorizationSourceEnum.HUMAN]: QuestionMarkCircleIcon,
    [CategorizationSourceEnum.CATEGORIZATION_ENGINE]: SparklesIcon,
    [CategorizationSourceEnum.MATCH_DERIVATION]: LinkIcon,
    [CategorizationSourceEnum.MERGE]: ArrowsRightLeftIcon,
    [CategorizationSourceEnum.BUSINESS_RULE]: AdjustmentsHorizontalIcon,
    [CategorizationSourceEnum.AUTOMATION_RULE]: BoltIcon,
    [CategorizationSourceEnum.AUTOMATION_RULE_BACKFILL]: BoltIcon,
    [CategorizationSourceEnum.INTERNAL_TRANSFER_DETECTION]: ArrowsRightLeftIcon,
    [CategorizationSourceEnum.SAVINGS_MIRROR]: BanknotesIcon,
    [CategorizationSourceEnum.CREDIT_CARD_SETTLEMENT]: CreditCardIcon,
    [CategorizationSourceEnum.UNKNOWN]: QuestionMarkCircleIcon,
};

function isAutomationRuleSource(source: string): boolean {
    return source === CategorizationSourceEnum.AUTOMATION_RULE
        || source === CategorizationSourceEnum.AUTOMATION_RULE_BACKFILL;
}

function initialsFromName(name: string): string {
    return name
        .split(' ')
        .filter(part => part.length > 0)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('');
}

function formatChangedOn(isoDate: string | null | undefined): string {
    if (!isoDate) return "";

    const changedOn = new Date(isoDate);
    if (isNaN(changedOn.getTime())) return "";

    const time = changedOn.toLocaleTimeString("nl-NL", {hour: "2-digit", minute: "2-digit"});
    return `${formatDate(isoDate)} ${time}`;
}

function CategorizationChangeLayout({leading, label, labelClassName, changedOn}: {
    leading: React.ReactNode,
    label: string,
    labelClassName: string,
    changedOn: string | null | undefined,
}) {
    const formattedChangedOn = formatChangedOn(changedOn);

    return (
        <span className="inline-flex min-w-0 items-center gap-2.5">
            {leading}
            <span className="flex min-w-0 flex-col gap-0.5">
                <span className={`truncate text-sm leading-tight ${labelClassName}`}>{label}</span>
                {formattedChangedOn && (
                    <span
                        className="truncate text-xs leading-tight text-gray-400"
                        data-testid="categorization-change-timestamp"
                    >
                        {formattedChangedOn}
                    </span>
                )}
            </span>
        </span>
    );
}

function CategorizationChangeCell({categorizationChange}: {categorizationChange: CategorizationChange}) {
    const source = categorizationChange.source as CategorizationSourceEnum;

    if (categorizationChange.source === CategorizationSourceEnum.HUMAN && categorizationChange.human_display_name) {
        return (
            <CategorizationChangeLayout
                leading={
                    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-superliteprimary text-xs font-semibold text-primary">
                        {initialsFromName(categorizationChange.human_display_name)}
                    </span>
                }
                label={categorizationChange.human_display_name}
                labelClassName="text-gray-900"
                changedOn={categorizationChange.changed_on}
            />
        );
    }

    const SourceIcon = CATEGORIZATION_SOURCE_ICONS[source] ?? QuestionMarkCircleIcon;
    const label = CATEGORIZATION_SOURCE_LABELS[source]
        ?? CATEGORIZATION_SOURCE_LABELS[CategorizationSourceEnum.UNKNOWN];
    const sourceBadge = (
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
            <SourceIcon className="size-4 text-gray-500" />
        </span>
    );

    if (isAutomationRuleSource(categorizationChange.source) && categorizationChange.automation_rule_id) {
        return (
            <Link to={automationRuleSettingsPath(categorizationChange.automation_rule_id)} className="inline-flex min-w-0">
                <CategorizationChangeLayout
                    leading={sourceBadge}
                    label={label}
                    labelClassName="text-primary hover:underline"
                    changedOn={categorizationChange.changed_on}
                />
            </Link>
        );
    }

    return (
        <CategorizationChangeLayout
            leading={sourceBadge}
            label={label}
            labelClassName="text-gray-900"
            changedOn={categorizationChange.changed_on}
        />
    );
}

function highlight(text: string | null | undefined, term: string | undefined) {
    if (!text) return "";
    if (!term) return text;
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\,]/g, (match) => (match === '.' || match === ',') ? '[.,]' : '\\' + match);
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    const parts = text.split(regex);
    const matchRegex = new RegExp(`^${escapedTerm}$`, 'i');
    return (
        <span>
            {parts.map((part, i) =>
                matchRegex.test(part) ? (
                    <mark key={i} className="bg-yellow-200 p-0 rounded-sm">{part}</mark>
                ) : (
                    part
                )
            )}
        </span>
    );
}

const NO_CATEGORIZATION_CHANGES: Record<string, CategorizationChange> = {};
const NO_TRANSACTION_IDS: Set<string> = new Set();

interface Props {
    transactions: Array<Transaction>,
    bankAccounts: Array<CustomerBankAccount>,
    onWrongGLAClick?: (transaction: Transaction) => void,
    onUndoBookAwayClick?: (transaction: Transaction) => void,
    renderRowActions?: (transaction: Transaction) => React.ReactNode,
    viewerRole: TransactionsTableViewerRole,
    showsCategorizationChange?: boolean,
    categorizationChangeById?: Record<string, CategorizationChange>,
    pendingCategorizationChangeIds?: Set<string>,
    resolvedTransactionIds?: Set<string>,
    onVisibleTransactionIdsChange?: (ids: string[]) => void,

    title?: string,
    footer?: React.ReactNode,
    emptyRowMessage?: string,
    serverSortableFields?: ReadonlyArray<string>,
    paginationState?: [
        paginationState: PaginationState<Transaction>,
        setPaginationState: (paginationState: any) => void
    ],
    sortingState?: [
        sortingState: SortingState,
        setSortingState: (sortingState: any) => void
    ],
    inMemoryPaginationUpdater?: (currentPage: number, pageSize: number) => void,
    onQueryUpdate: (pagination: PaginationState<Transaction> | null, sorting: SortingState | null) => void,
    highlightText?: string,
    isLoading?: boolean,
}

export function TransactionsTable({
                                      transactions,
                                      bankAccounts,
                                      onWrongGLAClick,
                                      onUndoBookAwayClick,
                                      renderRowActions,
                                      viewerRole,
                                      showsCategorizationChange,
                                      categorizationChangeById = NO_CATEGORIZATION_CHANGES,
                                      pendingCategorizationChangeIds = NO_TRANSACTION_IDS,
                                      resolvedTransactionIds = NO_TRANSACTION_IDS,
                                      onVisibleTransactionIdsChange,
                                      title,
                                      footer,
                                      emptyRowMessage,
                                      serverSortableFields,
                                      paginationState,
                                      sortingState,
                                      onQueryUpdate,
                                      highlightText,
                                      isLoading
                                  }: Props) {
    const showsBookAwayState = viewerRole === TransactionsTableViewerRole.PARTNER;

    const bankAccountsById = React.useMemo(
        () => new Map(bankAccounts.map(account => [account.id, account])),
        [bankAccounts],
    );

    const visibleTransactions = paginationState?.[0]?.selectedItemsInMemory;
    const lastReportedVisibleIdsRef = React.useRef<string[]>([]);

    React.useEffect(() => {
        if (!onVisibleTransactionIdsChange || !visibleTransactions) {
            return;
        }

        const ids = visibleTransactions.map(transaction => transaction.id);
        const lastReportedIds = lastReportedVisibleIdsRef.current;
        const isUnchanged = ids.length === lastReportedIds.length
            && ids.every((id, index) => id === lastReportedIds[index]);

        if (isUnchanged) {
            return;
        }

        lastReportedVisibleIdsRef.current = ids;
        onVisibleTransactionIdsChange(ids);
    }, [visibleTransactions, onVisibleTransactionIdsChange]);

    const columns = React.useMemo<Array<TableColumnSpec<Transaction>>>(() => {
        const allColumns: Array<TableColumnSpec<Transaction>> = [
            {
                name: 'Relatie',
                field: 'relationName',
                style: { skeleton: 'lead' },
                customRender: (row: Transaction) => (
                    <div className="flex items-center gap-3">
                        <RiExchangeLine className="h-6 w-6 shrink-0 text-gray-400" aria-hidden="true" />
                        <div className="text-sm text-gray-500">
                            {highlight(row.relationName, highlightText)}
                            <br/>
                            {highlight(row.relationIBAN, highlightText)}
                        </div>
                    </div>
                )
            },
            {
                name: 'Bedrag',
                field: 'value',
                style: {
                    widthMultiplier: 0.5,
                    align: 'right'
                },
                customRender: (row: Transaction) => (
                    <MoneyLabel value={row.value / 100} colorAid={true} highlightText={highlightText} />
                )
            },
            {
                name: 'Omschrijving',
                field: 'desc',
                customRender: (row: Transaction) => (
                    <div className="text-sm text-gray-500">
                        {highlight(row.desc, highlightText)}
                    </div>
                )
            },
            {
                name: 'Datum',
                field: 'bookingDate',
                style: {
                    nowrap: true,
                    widthMultiplier: 0.75
                },
                renderingPreset: RenderingPresetType.Date,
            },
            {
                name: viewerRole === TransactionsTableViewerRole.PARTNER    ? "Kleinboekrekening" : "Categorie",
                field: 'generalLedgerAccountId',
                style: {
                    nowrap: true,
                    widthMultiplier: 1.5
                },
                customRender: (row: Transaction) => {
                    const label = viewerRole === TransactionsTableViewerRole.PARTNER
                        ? row.generalLedgerAccount.label_nl
                        : row.generalLedgerAccount.cashflow_subcategory_nl;

                    const subledgerName = viewerRole === TransactionsTableViewerRole.PARTNER
                        ? row.generalLedgerAccount.subledger?.name
                        : undefined;

                    const isLockedByMatch = Boolean(row.reconciliationId);
                    const showsLockTooltip = isLockedByMatch && Boolean(onWrongGLAClick);
                    const vatBadgeLabel = vatCodeToBadgeLabel(row.vatCode);

                    return (
                        <div className="flex items-center gap-2">
                            <GLAPill
                                icon={row.generalLedgerAccount.icon}
                                label={label}
                                suffix={subledgerName && (
                                    <span className="text-gray-400"> · {subledgerName}</span>
                                )}
                                lockedByMatchTooltip={
                                    showsLockTooltip
                                        ? "Deze transactie is gekoppeld, de koppeling bepaalt de categorie. Open de match om de koppeling ongedaan te maken."
                                        : undefined
                                }
                                onWrongGLAClick={
                                    onWrongGLAClick && !isLockedByMatch
                                        ? () => onWrongGLAClick(row)
                                        : undefined
                                }
                            />
                            {vatBadgeLabel && (
                                <span data-testid="vat-badge">
                                    <Tag variant="secondary" size="xxs" className="whitespace-nowrap">
                                        {vatBadgeLabel}
                                    </Tag>
                                </span>
                            )}
                        </div>
                    );
                }
            },
            {
                name: 'Bank',
                field: 'accountId',
                style: {
                    nowrap: true,
                    widthMultiplier: 1.5
                },
                customRender: (row: Transaction) => {
                    const bankAccount = bankAccountsById.get(row.accountId);

                    return (
                        <div className="flex items-center gap-x-2 whitespace-nowrap">
                            <BankAccountLogo logoUrl={bankAccount?.logoUrl} accountType={bankAccount?.accountType} creditCardNumberPrefix={bankAccount?.creditCardNumberPrefix} className="h-6 w-6" />
                            <span>{bankAccount?.ownerName}</span>
                        </div>
                    );
                }
            },

            ...(renderRowActions || showsBookAwayState ? [{
                name: 'Status',
                field: 'id',
                style: {
                    nowrap: true,
                    widthMultiplier: 0.75
                },
                customRender: (row: Transaction) => (
                    <TransactionStatusCell
                        row={row}
                        showsBookAwayState={showsBookAwayState}
                        onUndoBookAwayClick={onUndoBookAwayClick}
                        renderRowActions={renderRowActions}
                    />
                )
            }] : []),

            ...(showsCategorizationChange ? [{
                name: 'Laatst gewijzigd door',
                field: 'categorizationChange',
                style: {
                    nowrap: true,
                    widthMultiplier: 1.25
                },
                disableSorting: pendingCategorizationChangeIds.size > 0,
                sortValue: (row: Transaction) => categorizationChangeById[row.id]?.changed_on ?? null,
                customRender: (row: Transaction) => {
                    const categorizationChange = categorizationChangeById[row.id];

                    if (categorizationChange) {
                        return <CategorizationChangeCell categorizationChange={categorizationChange} />;
                    }

                    if (pendingCategorizationChangeIds.has(row.id) || !resolvedTransactionIds.has(row.id)) {
                        return <div className="h-4 w-24 animate-pulse rounded bg-gray-100" data-testid="categorization-change-loading" />;
                    }

                    return <span className="text-sm text-gray-400">-</span>;
                }
            }] : []),

        ];

        if (!serverSortableFields) {
            return allColumns;
        }

        return allColumns.map(column => ({
            ...column,
            disableSorting: !serverSortableFields.includes(column.field),
        }));
    }, [
        highlightText, viewerRole, showsBookAwayState, bankAccountsById, onWrongGLAClick, onUndoBookAwayClick,
        renderRowActions, showsCategorizationChange, categorizationChangeById, pendingCategorizationChangeIds,
        resolvedTransactionIds, serverSortableFields,
    ]);

    const tableSpec: TableHeaderSpec<Transaction> = {
        title: title ?? `${transactions.length} Transacties`,
        isLoading: isLoading,
        footer,
        emptyRowMessage,
        options: footer ? {disableBottomPaginationWidget: true} : undefined,
        columns,
        pagination: paginationState ? {
            inMemory: true,
            paginationState: paginationState,
            totalPages: Math.ceil(transactions.length / 50),
            defaultPageSize: 50,
        } : undefined,

        sorting: {
            sortingState: sortingState,
            inMemory: !serverSortableFields,
        },
        onQueryUpdate: onQueryUpdate,
    };

    const TableComponent = createTable<Transaction>(transactions, tableSpec);

    return (
        <>
            {TableComponent()}
        </>
    );
}
